import {useCallback, useEffect, useRef, useState} from 'react';

export const CONTENT_AUTOSAVE_DELAY = 1000;

function draftKey(value) {
    return JSON.stringify(value);
}

export default function useContentDraft({
    initialDraft,
    initialRevision,
    saveDraft,
    onSaved,
    delay = CONTENT_AUTOSAVE_DELAY,
}) {
    const [draft, setDraftState] = useState(initialDraft);
    const [revision, setRevisionState] = useState(initialRevision);
    const [status, setStatus] = useState('clean');
    const [error, setError] = useState(null);
    const [conflict, setConflict] = useState(null);
    const draftRef = useRef(initialDraft);
    const revisionRef = useRef(initialRevision);
    const savedKeyRef = useRef(draftKey(initialDraft));
    const timeoutRef = useRef(null);
    const queueRef = useRef(Promise.resolve());
    const mountedRef = useRef(true);
    const onSavedRef = useRef(onSaved);

    useEffect(() => {
        onSavedRef.current = onSaved;
    }, [onSaved]);

    const setRevision = useCallback(value => {
        revisionRef.current = value;
        if (mountedRef.current) setRevisionState(value);
    }, []);

    const replaceDraft = useCallback((nextDraft, nextRevision) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        draftRef.current = nextDraft;
        savedKeyRef.current = draftKey(nextDraft);
        setDraftState(nextDraft);
        setRevision(nextRevision);
        setStatus('clean');
        setError(null);
        setConflict(null);
    }, [setRevision]);

    const setDraft = useCallback(next => {
        const value = typeof next === 'function' ? next(draftRef.current) : next;
        draftRef.current = value;
        setDraftState(value);
        setStatus(draftKey(value) === savedKeyRef.current ? 'clean' : 'dirty');
        setError(null);
        setConflict(null);
    }, []);

    const performSave = useCallback(async (intent = 'autosave') => {
        const payload = draftRef.current;
        const payloadKey = draftKey(payload);
        if (payloadKey === savedKeyRef.current) {
            return {etag: revisionRef.current, data: null, skipped: true};
        }
        if (mountedRef.current) setStatus('saving');
        try {
            const result = await saveDraft(payload, revisionRef.current, intent);
            savedKeyRef.current = payloadKey;
            setRevision(result.etag);
            if (mountedRef.current) onSavedRef.current?.(result.data);
            if (mountedRef.current) {
                const hasNewerChanges = draftKey(draftRef.current) !== payloadKey;
                setStatus(hasNewerChanges ? 'dirty' : 'saved');
                setError(null);
            }
            return result;
        } catch (saveError) {
            if (mountedRef.current) {
                setError(saveError);
                if (saveError.status === 412) {
                    setConflict(saveError.data);
                    setStatus('conflict');
                } else if (!saveError.status || saveError.name === 'TypeError') {
                    setStatus('offline');
                } else {
                    setStatus('invalid');
                }
            }
            throw saveError;
        }
    }, [saveDraft, setRevision]);
    const performSaveRef = useRef(performSave);

    useEffect(() => {
        performSaveRef.current = performSave;
    }, [performSave]);

    const saveNow = useCallback((intent = 'manual') => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        const queued = queueRef.current.catch(() => undefined)
            .then(() => performSave(intent));
        queueRef.current = queued;
        return queued;
    }, [performSave]);

    const retryConflict = useCallback(() => {
        const serverRevision = conflict?.revision;
        if (!serverRevision) {
            return Promise.reject(new Error('The server revision is unavailable.'));
        }
        const nextRevision = String(serverRevision).startsWith('"')
            ? String(serverRevision)
            : `"${serverRevision}"`;
        setRevision(nextRevision);
        setConflict(null);
        setError(null);
        setStatus('dirty');
        return saveNow('manual');
    }, [conflict, saveNow, setRevision]);

    useEffect(() => {
        if (status !== 'dirty') return undefined;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            saveNow('autosave').catch(() => undefined);
        }, delay);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [delay, draft, saveNow, status]);

    useEffect(() => {
        const warnBeforeUnload = event => {
            if (!['dirty', 'saving', 'offline', 'conflict'].includes(status)) return;
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () => window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [status]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (draftKey(draftRef.current) !== savedKeyRef.current) {
                queueRef.current.catch(() => undefined)
                    .then(() => performSaveRef.current('navigation')).catch(() => undefined);
            }
        };
    }, []);

    return {
        conflict,
        draft,
        error,
        replaceDraft,
        retryConflict,
        setRevision,
        revision,
        saveNow,
        setDraft,
        status,
    };
}
